import { lazy, Suspense, useEffect, useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { useHydrated } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import { useNoticeDialogOpen } from './ngo-notice';
import { SectionBoundary } from './section-states';

const ContractSignedDialog = lazy(() =>
	import('./contracts/contract-signed-dialog').then((m) => ({ default: m.ContractSignedDialog })),
);

/**
 * Comms' step 7B: every user of the NGO area sees the "agreement signed" pop-up once, on whichever page they enter,
 * for 30 days after the signature (Cristina Duran, demo of 2026-10-19). Signing online refreshes it, so the signer
 * sees it as soon as the signing page returns.
 */
export function ContractSignedNotice() {
	// A pop-up that fails to load shows nothing, rather than taking the page down with it.
	return (
		<SectionBoundary fallback={null} errorRender={() => null}>
			<ContractSignedNoticeContent />
		</SectionBoundary>
	);
}

function ContractSignedNoticeContent() {
	const queryClient = useQueryClient();
	// Fetched with the page and never saved in the browser (`isContractNoticeQueryKey`), like the signature notice.
	// It also waits for what the notices beside it read, so it renders in the same pass as they do; `settled` below
	// then lets their dialogs say whether they opened. The three load in parallel, and the values are not used here.
	const [, , { data: notice }] = useSuspenseQueries({
		queries: [
			trpc.ngo.getProfile.queryOptions(),
			trpc.contract.notice.queryOptions(),
			trpc.contract.signedNotice.queryOptions(),
		],
	});
	const acknowledge = useMutation(
		trpc.contract.acknowledgeSignedNotice.mutationOptions({
			// A refetch still in flight would write the closed pop-up back.
			onMutate: () => queryClient.cancelQueries({ queryKey: trpc.contract.signedNotice.queryKey() }),
			// Gone for this visit too, so the layout mounting again does not open it from the cache.
			onSuccess: () => queryClient.setQueryData(trpc.contract.signedNotice.queryKey(), null),
		}),
	);
	// Closed at once, while the server records it: the dialog plays its exit instead of waiting for the reply.
	const [closedId, setClosedId] = useState<string>();
	const hydrated = useHydrated();
	// One notice dialog at a time: the ones that ask the NGO to act go first, and this one waits while one is open.
	// It starts a render after hydration, once the dialogs beside it have said whether they opened.
	const otherDialogOpen = useNoticeDialogOpen();
	const [settled, setSettled] = useState(false);
	useEffect(() => {
		if (hydrated) setSettled(true);
	}, [hydrated]);
	if (!settled || !notice) return null;

	const close = () => {
		setClosedId(notice.contractId);
		acknowledge.mutate({ contractId: notice.contractId });
	};

	return (
		<Suspense fallback={null}>
			<ContractSignedDialog open={closedId !== notice.contractId && !otherDialogOpen} onClose={close} />
		</Suspense>
	);
}
